import React, { useState } from "react";
import {
  Button,
  Card,
  Table,
  Form,
  Input,
  Select,
  message,
  Tabs,
  Tag,
  DatePicker,
  Statistic,
  Row,
  Col,
  Drawer,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  BankOutlined,
  TransactionOutlined,
  CheckCircleOutlined,
  StopOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchBankAccounts_api,
  AddBankAccount_api,
  UpdateBankAccount_api,
  FetchBankTransactions_api,
  AddBankTransaction_api,
} from "../../Api/Bank/bank_api";
import { FetchUsers_api } from "../../Api/Users/user_management";
import { useAuthChecker } from "../../Context";
import { copyToClipboard } from "../../lib";
import dayjs from "dayjs";

const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

export const BankManagement = () => {
  const { user, isAdmin } = useAuthChecker();
  const [activeTab, setActiveTab] = useState("accounts");

  // --- Accounts State ---
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [accountForm] = Form.useForm();

  // --- Transactions State ---
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [transactionSubmitting, setTransactionSubmitting] = useState(false);
  const [transactionForm] = Form.useForm();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchDates, setSearchDates] = useState<[string, string] | null>(null);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [filterBank, setFilterBank] = useState<number | undefined>(undefined);
  const [filterUser, setFilterUser] = useState<number | undefined>(undefined);

  // --- Queries ---
  const {
    data: accountsData,
    refetch: refetchAccounts,
    isFetching: isFetchingAccounts,
  } = useQuery({
    queryKey: ["bank_accounts"],
    queryFn: FetchBankAccounts_api,
  });

  const {
    data: transactionsData,
    refetch: refetchTransactions,
    isFetching: isFetchingTransactions,
  } = useQuery({
    queryKey: [
      "bank_transactions",
      page,
      pageSize,
      searchDates,
      filterType,
      filterBank,
      filterUser,
    ],
    queryFn: () =>
      FetchBankTransactions_api(page, pageSize, {
        fromDate: searchDates ? searchDates[0] : "",
        toDate: searchDates ? searchDates[1] : "",
        type: filterType,
        bank_id: filterBank,
        user_id: filterUser,
      }),
    enabled: activeTab === "transactions",
  });

  const { data: usersData } = useQuery({
    queryKey: ["system_users_list"],
    queryFn: () => FetchUsers_api(1, 100, ""),
  });

  // Derived filtered accounts for dropdowns
  const ownAccounts =
    accountsData?.data?.filter((acc: any) => acc.isOwn == 1) || [];

  // --- Account Handlers ---
  const handleAccountSubmit = async (values: any) => {
    setAccountSubmitting(true);
    try {
      const payload = { ...values, user_id: user?.userId };
      if (editingAccount) {
        payload.id = editingAccount.id;
        const res = await UpdateBankAccount_api(payload);
        if (res.isDataUpdated) {
          message.success(res.msg);
          setAccountModalOpen(false);
          refetchAccounts();
        } else {
          message.error(res.msg);
        }
      } else {
        const res = await AddBankAccount_api(payload);
        if (res.isDataAdded) {
          message.success(res.msg);
          setAccountModalOpen(false);
          refetchAccounts();
        } else {
          message.error(res.msg);
        }
      }
    } finally {
      setAccountSubmitting(false);
    }
  };

  const accountColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 60 },
    {
      title: "Bank Info",
      key: "bank_info",
      render: (r: any) => (
        <div>
          <div className="font-bold">{r.account_name}</div>
          <div className="text-gray-500 text-xs">{r.bank_name}</div>
        </div>
      ),
    },
    {
      title: "Account Number",
      dataIndex: "account_number",
      key: "acc_no",
      render: (text) => (
        <span>
          {text}
          <CopyOutlined
            style={{ marginLeft: 8, cursor: "pointer", color: "#1890ff" }}
            onClick={() => {
              copyToClipboard(text);
              message.success("Account number copied!");
            }}
          />
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "isOwn",
      render: (val: any) =>
        val == 1 ? (
          <Tag color="blue">Internal</Tag>
        ) : (
          <Tag color="orange">External</Tag>
        ),
    },
    {
      title: "Balance (Rs)",
      dataIndex: "balance",
      render: (val: any) => parseFloat(val || 0).toLocaleString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: any) =>
        val === "active" ? (
          <CheckCircleOutlined className="text-green-500" />
        ) : (
          <StopOutlined className="text-red-500" />
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (r: any) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingAccount(r);
            accountForm.setFieldsValue(r);
            setAccountModalOpen(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  // --- Transaction Handlers ---
  const handleTransactionSubmit = async (values: any) => {
    setTransactionSubmitting(true);
    try {
      const payload = {
        ...values,
        date: values?.date?.format("YYYY-MM-DD HH:mm:ss"),
        user_id: user?.userId,
      };

      const res = await AddBankTransaction_api(payload);
      if (res.isDataAdded) {
        message.success(res.msg);
        setTransactionModalOpen(false);
        transactionForm.resetFields();
        refetchTransactions();
        refetchAccounts();
      } else {
        message.error(res.msg);
      }
    } finally {
      setTransactionSubmitting(false);
    }
  };

  const transactionColumns = [
    {
      title: "Details",
      key: "details",
      render: (r: any) => (
        <div>
          <div className="font-bold text-gray-700">
            {r?.transaction_type}
            {r?.transactionfee > 0 && (
              <span className="text-xs text-red-400 ml-2">
                (Fee: {r?.transactionfee})
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">{r?.date}</div>
        </div>
      ),
    },
    {
      title: "From -> To",
      key: "flow",
      render: (r: any) => (
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="font-medium">
              {r?.from_bank_name ||
                (r?.from_bank_id ? "Unknown" : "External / Cash")}
            </div>
            {r?.from_account_number && (
              <div className="text-xs text-gray-400">
                {r?.from_account_number}
              </div>
            )}
          </div>
          <div className="text-gray-300">→</div>
          <div>
            <div className="font-medium">
              {r?.to_bank_name ||
                (r?.to_bank_id ? "Unknown" : "External / Cash")}
            </div>
            {r?.to_account_number && (
              <div className="text-xs text-gray-400">
                {r?.to_account_number}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Amount (Rs)",
      dataIndex: "amount",
      align: "right" as const,
      render: (val: any, r: any) => (
        <span
          className={
            r?.transaction_type === "DEPOSIT"
              ? "text-green-600 font-bold"
              : "text-red-500 font-bold"
          }
        >
          {parseFloat(val || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "username",
      render: (u: any) => <Tag>{u || "System"}</Tag>,
    },
    { title: "Remarks", dataIndex: "other_details" },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Bank</h1>
          <p className="text-gray-500">
            Bank accounts and transactions management
          </p>
        </div>

        <div className="flex gap-4">
          {activeTab === "accounts" ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingAccount(null);
                accountForm.resetFields();
                setAccountModalOpen(true);
              }}
            >
              Add Bank Account
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<TransactionOutlined />}
              onClick={() => {
                transactionForm.resetFields();
                setTransactionModalOpen(true);
              }}
            >
              New Transaction
            </Button>
          )}
        </div>
      </div>
      <div className="mb-6">
        <Row gutter={24}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Active Internal Accounts"
                value={ownAccounts.length}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Liquid Balance"
                value={ownAccounts.reduce(
                  (acc: number, curr: any) =>
                    acc + parseFloat(curr.balance || 0),
                  0,
                )}
                precision={2}
                prefix="Rs."
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    Bank Activity Date Filter
                  </span>
                  <div className="flex gap-2">
                    <DatePicker 
                      placeholder="From"
                      className="h-[32px]"
                      value={searchDates?.[0] ? dayjs(searchDates[0]) : null}
                      onChange={(date) => {
                        const start = date ? date.format("YYYY-MM-DD") : "";
                        const end = searchDates?.[1] || "";
                        setSearchDates([start, end]);
                        setPage(1);
                      }}
                    />
                    <DatePicker 
                      placeholder="To"
                      className="h-[32px]"
                      value={searchDates?.[1] ? dayjs(searchDates[1]) : null}
                      onChange={(date) => {
                        const start = searchDates?.[0] || "";
                        const end = date ? date.format("YYYY-MM-DD") : "";
                        setSearchDates([start, end]);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {activeTab === "transactions" && (
        <Card className="shadow-sm rounded-xl border-none mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                Transaction Type
              </p>
              <Select
                placeholder="All Types"
                className="w-full"
                allowClear
                value={filterType}
                onChange={(val) => {
                  setFilterType(val);
                  setPage(1);
                }}
              >
                <Option value="DEPOSIT">Deposit (In)</Option>
                <Option value="WITHDRAW">Withdraw (Out)</Option>
                <Option value="TRANSFER">Internal Transfer</Option>
              </Select>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                Bank / Account
              </p>
              <Select
                placeholder="Select Account..."
                className="w-full"
                showSearch
                allowClear
                optionFilterProp="children"
                value={filterBank}
                onChange={(val) => {
                  setFilterBank(val);
                  setPage(1);
                }}
              >
                {accountsData?.data?.map((acc: any) => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.bank_name} - {acc.account_number}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
                Created By (User)
              </p>
              <Select
                placeholder="All Users"
                className="w-full"
                showSearch
                allowClear
                optionFilterProp="children"
                value={filterUser}
                onChange={(val) => {
                  setFilterUser(val);
                  setPage(1);
                }}
              >
                {usersData?.users?.map((u: any) => (
                  <Option key={u.id} value={u.id}>
                    {u.username}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                className="w-full"
                onClick={() => {
                  setFilterType(undefined);
                  setFilterBank(undefined);
                  setFilterUser(undefined);
                  setSearchDates(null);
                  setPage(1);
                }}
              >
                Reset All Filters
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Bank Accounts" key="accounts">
            <Table
              className="overflow-x-auto"
              dataSource={accountsData?.data || []}
              columns={accountColumns}
              loading={isFetchingAccounts}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true }}
            />
          </TabPane>
          <TabPane tab="Transactions" key="transactions">
            <Table
              className="overflow-x-auto"
              dataSource={transactionsData?.data || []}
              columns={transactionColumns}
              loading={isFetchingTransactions}
              rowKey="id"
              pagination={{
                current: page,
                pageSize: pageSize,
                total: transactionsData?.total || 0,
                showSizeChanger: true,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Account Modal */}
      <Drawer
        title={editingAccount ? "Edit Bank Account" : "Add Bank Account"}
        open={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
        footer={null}
      >
        <Form
          form={accountForm}
          layout="vertical"
          onFinish={handleAccountSubmit}
        >
          <Form.Item
            label="Bank Name"
            name="bank_name"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g. Commercial Bank" />
          </Form.Item>
          <Form.Item
            label="Account Name"
            name="account_name"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g. S.A. Perera" />
          </Form.Item>
          <Form.Item
            label="Account Number"
            name="account_number"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Account Type" name="isOwn" initialValue={1}>
                <Select>
                  <Option value={1}>Internal (Own)</Option>
                  <Option value={0}>External</Option>
                </Select>
              </Form.Item>
            </Col>
            {isAdmin && (
              <Col span={12}>
                <Form.Item
                  label="Initial Balance"
                  name="balance"
                  initialValue={0}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Form.Item label="Status" name="status" initialValue="active">
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end gap-2 text-right">
            <Button onClick={() => setAccountModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={accountSubmitting}
            >
              Save
            </Button>
          </div>
        </Form>
      </Drawer>

      {/* Transaction Modal */}
      <Drawer
        title="New Transaction"
        open={transactionModalOpen}
        onClose={() => setTransactionModalOpen(false)}
        footer={null}
      >
        <Form
          form={transactionForm}
          layout="vertical"
          onFinish={handleTransactionSubmit}
        >
          <Form.Item
            label="Transaction Type"
            name="transaction_type"
            initialValue="DEPOSIT"
          >
            <Select
              onChange={() =>
                transactionForm.setFieldsValue({
                  from_bank_id: null,
                  to_bank_id: null,
                })
              }
            >
              <Option value="DEPOSIT">Deposit (In)</Option>
              <Option value="WITHDRAW">Withdraw (Out)</Option>
              <Option value="TRANSFER">Internal Transfer</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev?.transaction_type !== curr?.transaction_type
            }
          >
            {({ getFieldValue }) => {
              const type = getFieldValue("transaction_type");
              if (type == "TRANSFER") {
                return (
                  <>
                    <Form.Item
                      label="From Account (Source)"
                      name="from_bank_id"
                      rules={[{ required: true }]}
                    >
                      <Select placeholder="Select source account">
                        {ownAccounts.map((acc: any) => (
                          <Option key={acc.id} value={acc.id}>
                            {acc.bank_name} - {acc.account_number} (Rs.
                            {acc.balance})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="To Account (Destination)"
                      name="to_bank_id"
                      rules={[{ required: true }]}
                    >
                      <Select placeholder="Select destination account">
                        {ownAccounts.map((acc: any) => (
                          <Option key={acc.id} value={acc.id}>
                            {acc.bank_name} - {acc.account_number}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                );
              } else if (type == "DEPOSIT") {
                return (
                  <>
                    <Form.Item
                      label="To Account (Internal)"
                      name="to_bank_id"
                      rules={[{ required: true }]}
                    >
                      <Select placeholder="Select account to deposit to">
                        {ownAccounts.map((acc: any) => (
                          <Option key={acc.id} value={acc.id}>
                            {acc.bank_name} - {acc.account_number}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {/* <Form.Item
                      label="From (Optional Source)"
                      name="from_bank_id"
                      help="Leave empty for Cash Deposit"
                    >
                      <Select
                        placeholder="External Account (Optional)"
                        allowClear
                      >
                        {accountsData?.data
                          ?.filter((a: any) => a.isOwn == 0)
                          .map((acc: any) => (
                            <Option key={acc.id} value={acc.id}>
                              {acc.bank_name} - {acc.account_name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item> */}
                  </>
                );
              } else {
                // WITHDRAW
                return (
                  <>
                    <Form.Item
                      label="From Account (Internal)"
                      name="from_bank_id"
                      rules={[{ required: true }]}
                    >
                      <Select placeholder="Select account to withdraw from">
                        {ownAccounts.map((acc: any) => (
                          <Option key={acc.id} value={acc.id}>
                            {acc.bank_name} - {acc.account_number} (Rs.
                            {acc.balance})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {/* <Form.Item
                      label="To (Optional Destination)"
                      name="to_bank_id"
                      help="Leave empty for Cash Withdrawal"
                    >
                      <Select
                        placeholder="External Account (Optional)"
                        allowClear
                      >
                        {accountsData?.data
                          ?.filter((a: any) => a.isOwn == 0)
                          .map((acc: any) => (
                            <Option key={acc.id} value={acc.id}>
                              {acc.bank_name} - {acc.account_name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item> */}
                  </>
                );
              }
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Amount"
                name="amount"
                rules={[{ required: true }]}
              >
                <Input prefix="Rs." type="number" step="0.01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Date & Time"
                name="date"
                rules={[{ required: true }]}
              >
                <DatePicker showTime className="w-full" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Transaction Fee"
            name="transactionfee"
            initialValue="0"
          >
            <Input prefix="Rs." type="number" step="0.01" />
          </Form.Item>

          <Form.Item label="Remarks / Details" name="other_details">
            <Input.TextArea rows={2} />
          </Form.Item>

          <div className="flex justify-end gap-2 text-right">
            <Button onClick={() => setTransactionModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={transactionSubmitting}
            >
              Record Transaction
            </Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};
